import {useIntl} from 'react-intl'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useUGCMedia} from '../../../../../hooks/use-ugc-media'
import {UGC_PLACEMENT} from '../../../../../constants'
import {useBrand} from '../../../../../contexts/brand-context'
import {isUgcPlacementEnabled, getUgcHomePageLimit} from '../../../../../utils/ugc-flags'
import {buildUgcLandingPath} from '../../../../../utils/url'

import {isUgcSource} from './helpers'

/**
 * Converts the UGC carousel dynamic source into renderable `UgcTileBlock`
 * pseudo-tiles. The item cap is managed by the `BV_UGC_HOME_PAGE_LIMIT` site
 * preference (an explicit `limit` argument still wins when supplied) and the
 * collection/cgid comes from `ProductCarouselGridDynamicContentReference`.
 * Non-UGC dynamic sources return an empty list and do not fetch.
 *
 * The source is gated on the master `BV_UGC_ENABLED` flag so a disabled brand
 * never issues a request. The BV UGC feed is brand- and locale-scoped, so the
 * resolved `brandId` and the active locale ride along on the request (the
 * client fetch to `/api/bv-ugc/media` carries no path locale of its own).
 *
 * @param {?string} dynamicContent - CMS dynamic source label for the carousel.
 * @param {object} [options] - `limit` (explicit override), `cgid` (collection
 *   id used to build expanded-view links) and `placement` (defaults to
 *   `homepage`).
 * @returns {{tiles: Array<object>, isLoading: boolean}}
 */
export const useUgcTiles = (
    dynamicContent,
    {limit, cgid, placement = UGC_PLACEMENT.HOMEPAGE} = {}
) => {
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const {brandId} = useBrand()
    const {locale} = useIntl()
    const placementEnabled = isUgcPlacementEnabled(appConfig, placement, site?.id, brandId)
    const shouldFetch = isUgcSource(dynamicContent) && placementEnabled
    const explicitLimit = Number(limit)
    const effectiveLimit = explicitLimit > 0 ? explicitLimit : getUgcHomePageLimit(appConfig)
    const {data, isLoading} = useUGCMedia(
        {
            placement,
            limit: effectiveLimit,
            brand: brandId,
            locale: locale?.replace('_', '-')
        },
        {enabled: shouldFetch}
    )

    if (!shouldFetch) return {tiles: [], isLoading: false, landingHref: undefined}

    const items = Array.isArray(data?.items) ? data.items : []
    return {
        tiles: items.map((item) => ({
            __typename: 'UgcTileBlock',
            item,
            cgid: cgid || undefined,
            ContentLink: {GuidValue: `ugc-${item.id}`}
        })),
        isLoading,
        landingHref: buildUgcLandingPath(brandId, cgid)
    }
}
